import { countryCodeEmoji } from 'country-code-emoji'
import { Company } from 'entities/company'
import { Job } from 'entities/job'
import { Location } from 'entities/location'
import { formatRemuneration } from 'entities/remuneration'
import { Tag } from 'entities/tag'
import { StandardVertical, companyVertical } from 'entities/vertical'
import { routes } from 'routes'
import { emoji } from 'lib/emoji'
import { toImgixUrl } from 'lib/imgix'
import { removeNullAndUndefined } from 'lib/object'
import { canonicalUrl as seoCanonicalUrl, textToSeoDescription } from 'lib/seo'
import { capitalize } from 'lib/text'
import { VerticalConfig } from 'lib/vertical'
import { countries } from 'containers/job/components/seo/countries'

export type JobForSeo = Pick<
    Job,
    | 'title'
    | 'remote'
    | 'remuneration'
    | 'tagline'
    | 'verticals'
    | 'slug'
    | 'roleType'
    | 'lastModified'
    | 'descriptionHtml'
    | 'remoteInfo'
    | 'yearsOfCommercialExperience'
> & {
    company: Pick<Company, 'name' | 'logo'>
    location?: Pick<Location, 'city' | 'country' | 'countryCode' | 'postCode'>
    tags: Pick<Tag, 'label'>[]
}

const isRemote = (job: JobForSeo): boolean =>
    job.remote || job.title.search(/remote/i) !== -1

export const title = (job: JobForSeo) => {
    let t = job.title
    if (isRemote(job)) {
        t = `Remote ${t}`
    } else if (job?.location?.city) {
        t = `${t} in ${job?.location?.city}`
    }
    return `${t} - ${job.company.name}`
}

//

export const description = (job: JobForSeo) => {
    const remote = isRemote(job) ? 'Remote' : undefined
    const location = remote
        ? undefined
        : job?.location?.city && `in ${job?.location?.city}`
    const remuneration = job.remuneration && `${formatRemuneration(job.remuneration)}`
    const tags = job.tags
        .map(t => capitalize(t.label))
        .filter(Boolean)
        .join(', ')
    const tagline = `${job.tagline}`

    return textToSeoDescription(
        [remote, job.title.trim(), location, remuneration, tags, tagline]
            .filter(Boolean)
            .join(', '),
    )
}

//

export const image = (job: JobForSeo) =>
    job.company.logo ? toImgixUrl(job.company.logo, { w: 128 }) : '#'

//

export const keywords = (
    vc: Pick<VerticalConfig, 'labelName'>,
    job: JobForSeo,
): string[] => {
    return [
        job.tags.map(t => t.label),
        job.remote && 'Remote',
        job.location?.city,
        job.location?.country,
        `${vc.labelName} job`,
    ]
        .flat()
        .filter(Boolean) as string[]
}

//

export const canonicalUrl = (job: JobForSeo) => {
    const vertical = job.verticals[0]
    return seoCanonicalUrl(vertical, routes.job({ slug: job.slug }))
}

//

export const shouldNoIndexAndNoFollow = (
    currentVertical: StandardVertical,
    job: JobForSeo,
) => {
    return (
        currentVertical === companyVertical ||
        (currentVertical === 'remote' && !isRemote(job))
    )
}

//

export const twitterData = (job: JobForSeo): [string, string][] => {
    const location = job.location
    return [
        [
            'Salary & Location',
            [
                [
                    emoji.bagOfMoney,
                    job.remuneration && formatRemuneration(job.remuneration),
                ].join(' '),
                location?.countryCode && countryCodeEmoji(location?.countryCode),
                [location?.city, location?.country].filter(Boolean).join(', '),
            ]
                .filter(Boolean)
                .join(', '),
        ],
        [
            'Skills',
            emoji.rocket + ' ' + job.tags.map(t => capitalize(t.label)).join(', '),
        ],
    ]
}

//

export const structuredDataEmploymentType = (job: JobForSeo) => {
    switch (job.roleType) {
        case 'Contract':
            return 'CONTRACTOR'
        case 'Full time':
            return 'FULL_TIME'
        case 'Intern':
            return 'INTERN'
    }
}

export const structuredJobLocationType = (job: JobForSeo) => {
    return isRemote(job) ? 'TELECOMMUTE' : undefined
}

export const structuredExperienceRequirements = (job: JobForSeo) => {
    return job.yearsOfCommercialExperience
        ? {
              '@type': 'OccupationalExperienceRequirements',
              monthsOfExperience: job.yearsOfCommercialExperience,
          }
        : 'no requirements'
}

export const structuredApplicantLocationRequirements = (job: JobForSeo) => {
    return job.remoteInfo?.regionRestrictions?.length
        ? job.remoteInfo?.regionRestrictions?.map(restriction => ({
              '@type': 'Country',
              name: restriction,
          }))
        : countries.map(country => ({ '@type': 'Country', name: country.name }))
}

//

export const structuredDataLocation = (job: JobForSeo) => {
    const location = job.location
    return removeNullAndUndefined({
        '@type': 'PostalAddress',
        addressCountry: location?.countryCode,
        postalCode: location?.postCode,
        addressLocality: location?.city,
        addressRegion: location?.city,
    })
}

//

export const structuredDataDescription = (job: JobForSeo) => {
    return job.descriptionHtml.replace('h2>', 'p>')
}

//

export const structuredDataRemuneration = (job: JobForSeo) => {
    const remuneration = job.remuneration
    if (!remuneration?.competitive) {
        return {
            '@type': 'MonetaryAmount',
            currency: remuneration?.currency,
            value: {
                '@type': 'QuantitativeValue',
                value: remuneration?.min ?? remuneration?.max,
                unitText: remuneration?.timePeriod === 'Yearly' ? 'YEAR' : 'DAY',
                ...(remuneration?.max
                    ? { minValue: remuneration?.min, maxValue: remuneration?.max }
                    : undefined),
            },
        }
    }
}
